import { clearActionsForScope, clearLegacyActions } from './actionsStore';
import { buildNotesScopeKey, clearAllNotes, clearLegacyNotes, clearNotesForScope } from './notesStore';
import {
  clearAllPersistedTransactionNotes,
  clearPersistedTransactionNotesForScope,
} from './persistedTransactionNotes';
import { resetBootstrapped } from './bootstrapGuard';
import progressSignal from './progressSignal';
import reportsService from '../services/reportsService';
import { resetNetWorthServiceCache } from '../services/netWorthService';
import { purgeAllTrendsV2, purgeTrendsV2ForHousehold } from '../services/trendsV2CacheBridge';
import { clearDesktopTransactionSnapshots, clearDesktopSnapshotsForHousehold } from '../services/desktopTransactionSnapshots';

const LEGACY_LAST_HOUSEHOLD_ID_KEY = 'lastHouseholdId';
const LAST_HOUSEHOLD_ID_PREFIX = 'vylo:last-household-id';
const MAX_SAFE_HOUSEHOLD_ID = '9007199254740991';

const canonicalHouseholdId = (value) => {
  const raw = value == null ? '' : String(value);
  if (!/^[1-9]\d*$/.test(raw)
    || raw.length > MAX_SAFE_HOUSEHOLD_ID.length
    || (raw.length === MAX_SAFE_HOUSEHOLD_ID.length && raw > MAX_SAFE_HOUSEHOLD_ID)) return null;
  return raw;
};

export const buildLastHouseholdIdStorageKey = (userId) => {
  if (userId == null || userId === '') return null;
  return `${LAST_HOUSEHOLD_ID_PREFIX}:${String(userId)}`;
};

const localStorageKeys = () => {
  const keys = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key) keys.push(key);
  }
  return keys;
};

export const getRememberedHouseholdIdForUser = (userId) => {
  const storageKey = buildLastHouseholdIdStorageKey(userId);
  if (!storageKey) return null;
  const rememberedHouseholdId = localStorage.getItem(storageKey);
  if (rememberedHouseholdId == null) return null;
  const canonicalId = canonicalHouseholdId(rememberedHouseholdId);
  if (canonicalId) return canonicalId;

  localStorage.removeItem(storageKey);
  purgeAllTrendsV2();
  return null;
};

export const rememberHouseholdIdForUser = ({
  userId,
  householdId,
} = {}) => {
  const storageKey = buildLastHouseholdIdStorageKey(userId);
  if (!storageKey || householdId == null || householdId === '') return;
  localStorage.setItem(storageKey, String(householdId));
  localStorage.removeItem(LEGACY_LAST_HOUSEHOLD_ID_KEY);
};

export const clearLastHouseholdMarkers = ({
  userId = null,
} = {}) => {
  localStorage.removeItem(LEGACY_LAST_HOUSEHOLD_ID_KEY);

  const scopedKey = buildLastHouseholdIdStorageKey(userId);
  if (scopedKey) {
    localStorage.removeItem(scopedKey);
    return;
  }

  localStorageKeys()
    .filter((key) => key.startsWith(`${LAST_HOUSEHOLD_ID_PREFIX}:`))
    .forEach((key) => localStorage.removeItem(key));
};

export const clearAuthenticatedClientState = ({
  userId = null,
  householdId = null,
} = {}) => {
  clearDesktopTransactionSnapshots();
  resetNetWorthServiceCache();
  const currentNotesScope = buildNotesScopeKey(userId, householdId);
  if (currentNotesScope) {
    clearNotesForScope(currentNotesScope);
    clearPersistedTransactionNotesForScope(currentNotesScope);
  }

  clearAllNotes();
  clearAllPersistedTransactionNotes();
  clearLegacyNotes();
  reportsService.clearAll();
  purgeAllTrendsV2();
  resetBootstrapped();
  progressSignal.reset();
  clearLastHouseholdMarkers({ userId });
};

export const clearCrossHouseholdClientState = ({
  userId = null,
  previousHouseholdId = null,
  nextHouseholdId = null,
} = {}) => {
  const previousId = previousHouseholdId == null ? null : canonicalHouseholdId(previousHouseholdId);
  const nextId = nextHouseholdId == null ? null : canonicalHouseholdId(nextHouseholdId);

  if ((previousHouseholdId != null && !previousId) || (nextHouseholdId != null && !nextId)) {
    resetNetWorthServiceCache();
    clearDesktopTransactionSnapshots();
    purgeAllTrendsV2();
    return;
  }

  if (!previousId || previousId === nextId) {
    return;
  }

  clearActionsForScope(previousId);
  resetNetWorthServiceCache();
  clearDesktopSnapshotsForHousehold(previousId);
  clearLegacyActions();

  if (userId) {
    const previousNotesScope = buildNotesScopeKey(userId, previousId);
    clearNotesForScope(previousNotesScope);
    clearPersistedTransactionNotesForScope(previousNotesScope);
  }

  clearLegacyNotes();
  reportsService.clearAll();
  purgeTrendsV2ForHousehold(previousId);
};
